
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function BestellungPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    produkte: '',
    menge: '',
    lieferadresse: '',
    bemerkungen: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Bestellnummer generieren
      const bestellnummer = `F${Date.now()}`;
      
      // Bestelldaten vorbereiten
      const bestellungData = {
        bestellnummer,
        name: formData.name,
        email: formData.email,
        telefon: formData.telefon,
        adresse: formData.adresse,
        produkte: formData.produkte.split('\n').filter(p => p.trim()).map(produkt => ({
          name: produkt.trim(),
          preis: 0, // Preis wird später festgelegt
          menge: 1
        })),
        gesamtpreis: 0, // Wird später berechnet
        status: 'neu',
        datum: new Date().toISOString(),
        notizen: formData.notizen
      };

      // An Supabase senden
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/bestellungen-verwalten`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(bestellungData)
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern der Bestellung');
      }

      const result = await response.json();
      console.log('Bestellung gespeichert:', result);

      // Formular zurücksetzen
      setFormData({
        name: '',
        email: '',
        telefon: '',
        adresse: '',
        produkte: '',
        notizen: ''
      });

      // Zur Bestätigungsseite weiterleiten (NICHT zur Admin-Seite!)
      navigate('/bestellbestaetigung', { 
        state: { bestellung: bestellungData },
        replace: true 
      });

    } catch (error) {
      console.error('Fehler beim Absenden der Bestellung:', error);
      setSubmitMessage('Fehler beim Absenden der Bestellung. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <i className="ri-shopping-cart-line text-white text-xl"></i>
              </div>
              <h1 className="text-2xl font-black text-gray-900">Dä Snäckomat</h1>
            </div>
            <nav className="flex space-x-6">
              <a href="/" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Home</a>
              <a href="/shop" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Shop</a>
              <a href="/bestellung" className="text-blue-600 font-medium">Bestellung</a>
              <a href="/bestellungen-kunden" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Bestellungen Kunden</a>
            </nav>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Einfach bestellen
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Wählen Sie Ihre bevorzugte Bestellmethode - schnell, einfach und zuverlässig
          </p>
        </div>

        {/* Bestellmethoden */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* WhatsApp */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-whatsapp-line text-white text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">WhatsApp</h3>
            <p className="text-gray-600 mb-6 text-center">
              Schnell und direkt über WhatsApp bestellen
            </p>
            <a 
              href="https://wa.me/41791234567?text=Hallo,%20ich%20möchte%20gerne%20bestellen:"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-green-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              WhatsApp öffnen
            </a>
          </div>

          {/* Telefon */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-phone-line text-white text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Telefon</h3>
            <p className="text-gray-600 mb-6 text-center">
              Persönliche Beratung und Bestellung
            </p>
            <a 
              href="tel:+41791234567"
              className="w-full bg-blue-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Jetzt anrufen
            </a>
          </div>

          {/* E-Mail */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-line text-white text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">E-Mail</h3>
            <p className="text-gray-600 mb-6 text-center">
              Detaillierte Bestellung per E-Mail
            </p>
            <a 
              href="mailto:info@snaeckomat.ch?subject=Bestellung&body=Hallo,%0D%0A%0D%0AIch möchte gerne folgende Produkte bestellen:%0D%0A%0D%0A- Produkt 1:%0D%0A- Produkt 2:%0D%0A%0D%0AMeine Kontaktdaten:%0D%0AName:%0D%0ATelefon:%0D%0ALieferadresse:%0D%0A%0D%0AVielen Dank!"
              className="w-full bg-purple-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-purple-600 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              E-Mail senden
            </a>
          </div>
        </div>

        {/* Online Bestellformular */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Online Bestellformular</h2>
            <p className="text-gray-600">Oder füllen Sie einfach das Formular aus</p>
          </div>

          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                <p className="text-green-800 font-medium">
                  Vielen Dank! Ihre Bestellung wurde erfolgreich übermittelt. Sie werden automatisch zur Bestellübersicht weitergeleitet...
                </p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                <p className="text-red-800 font-medium">
                  Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form id="bestellung-form" action="https://readdy.ai/api/form/submit/bestellung-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Ihr vollständiger Name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="ihre@email.ch"
                />
              </div>

              <div>
                <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  id="telefon"
                  name="telefon"
                  value={formData.telefon}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="+41 79 123 45 67"
                />
              </div>

              <div>
                <label htmlFor="menge" className="block text-sm font-medium text-gray-700 mb-2">
                  Gewünschte Menge
                </label>
                <input
                  type="text"
                  id="menge"
                  name="menge"
                  value={formData.menge}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="z.B. 2x, 5 Stück, etc."
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="produkte" className="block text-sm font-medium text-gray-700 mb-2">
                Gewünschte Produkte *
              </label>
              <textarea
                id="produkte"
                name="produkte"
                value={formData.produkte}
                onChange={handleChange}
                required
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                placeholder="Bitte listen Sie hier die gewünschten Produkte auf..."
              />
              <p className="text-xs text-gray-500 mt-1">Maximal 500 Zeichen</p>
            </div>

            <div className="mb-6">
              <label htmlFor="lieferadresse" className="block text-sm font-medium text-gray-700 mb-2">
                Lieferadresse
              </label>
              <textarea
                id="lieferadresse"
                name="lieferadresse"
                value={formData.lieferadresse}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                placeholder="Straße, PLZ Ort"
              />
              <p className="text-xs text-gray-500 mt-1">Maximal 500 Zeichen</p>
            </div>

            <div className="mb-6">
              <label htmlFor="bemerkungen" className="block text-sm font-medium text-gray-700 mb-2">
                Bemerkungen
              </label>
              <textarea
                id="bemerkungen"
                name="bemerkungen"
                value={formData.bemerkungen}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                placeholder="Besondere Wünsche oder Anmerkungen..."
              />
              <p className="text-xs text-gray-500 mt-1">Maximal 500 Zeichen</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Wird gesendet...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line mr-2"></i>
                  Bestellung anfragen
                </>
              )}
            </button>
          </form>
        </div>

        {/* Kontakt Info */}
        <div className="bg-gray-50 rounded-2xl p-8 mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Kontakt</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <i className="ri-phone-line text-blue-500 text-2xl mb-2"></i>
              <p className="font-medium text-gray-900">Telefon</p>
              <p className="text-gray-600">+41 79 123 45 67</p>
            </div>
            <div>
              <i className="ri-mail-line text-blue-500 text-2xl mb-2"></i>
              <p className="font-medium text-gray-900">E-Mail</p>
              <p className="text-gray-600">info@snaeckomat.ch</p>
            </div>
            <div>
              <i className="ri-whatsapp-line text-blue-500 text-2xl mb-2"></i>
              <p className="font-medium text-gray-900">WhatsApp</p>
              <p className="text-gray-600">+41 79 123 45 67</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
